import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import type { BooleanInput } from '@angular/cdk/coercion';
import {
	afterNextRender,
	booleanAttribute,
	computed,
	contentChild,
	contentChildren,
	Directive,
	effect,
	forwardRef,
	inject,
	Injector,
	input,
	linkedSignal,
	model,
	signal,
	untracked,
} from '@angular/core';
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BrnFieldControl, provideBrnLabelable } from '@spartan-ng/brain/field';
import { type ChangeFn, type TouchFn } from '@spartan-ng/brain/forms';
import { BrnPopover } from '@spartan-ng/brain/popover';
import { BrnComboboxChipInput } from './brn-combobox-chip-input';
import { BrnComboboxContent } from './brn-combobox-content';
import { type BrnComboboxItem } from './brn-combobox-item';
import { BrnComboboxItemToken } from './brn-combobox-item.token';
import type { BrnComboboxList } from './brn-combobox-list';
import {
	type BrnComboboxBase,
	type ComboboxFilter,
	type ComboboxFilterOptions,
	ComboboxInputMode,
	type ComboboxItemEqualToValue,
	type ComboboxItemToString,
	injectBrnComboboxConfig,
	provideBrnComboboxBase,
} from './brn-combobox.token';

export const BRN_COMBOBOX_MULTIPLE_VALUE_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => BrnComboboxMultiple),
	multi: true,
};

@Directive({
	selector: '[brnCombobox]',
	providers: [
		BRN_COMBOBOX_MULTIPLE_VALUE_ACCESSOR,
		provideBrnComboboxBase(BrnComboboxMultiple),
		provideBrnLabelable(BrnComboboxMultiple),
	],
	hostDirectives: [BrnFieldControl],
	host: {
		'(focusout)': '_onFocusOut($event)',
	},
})
export class BrnComboboxMultiple<T> implements BrnComboboxBase<T>, ControlValueAccessor {
	private readonly _injector = inject(Injector);
	private readonly _fieldControl = inject(BrnFieldControl, { optional: true });

	private readonly _config = injectBrnComboboxConfig<T>();

	/** Access the popover if present */
	private readonly _brnPopover = inject(BrnPopover, { optional: true });

	public readonly controlState = this._fieldControl?.controlState;

	/** Whether the combobox is disabled */
	public readonly disabled = input<boolean, BooleanInput>(false, { transform: booleanAttribute });

	protected readonly _disabled = linkedSignal(this.disabled);

	/** @internal The disabled state as a readonly signal */
	public readonly disabledState = this._disabled.asReadonly();

	/** Options for filtering the combobox items */
	public readonly filterOptions = input<ComboboxFilterOptions>({});

	/** @internal The collator used for string comparison */
	public readonly collator = computed(() => {
		const options = this.filterOptions();
		const mergedOptions = { ...this._config.filterOptions, ...options };
		return new Intl.Collator(options.locale, mergedOptions);
	});

	/** A function to compare an item with the selected value. */
	public readonly isItemEqualToValue = input<ComboboxItemEqualToValue<T>>(this._config.isItemEqualToValue);

	/** A function to convert an item to a string for display. */
	public readonly itemToString = input<ComboboxItemToString<T> | undefined>(this._config.itemToString);

	/** A custom filter function to use when searching. */
	public readonly filter = input<ComboboxFilter<T>>(this._config.filter);

	/** Whether to auto-highlight the first matching item. */
	public readonly autoHighlight = input<boolean, BooleanInput>(this._config.autoHighlight, {
		transform: booleanAttribute,
	});

	/** Whether to reset the search and close the popover after selecting an item when the search is active. */
	public readonly closeOnSelect = input<boolean, BooleanInput>(this._config.closeOnSelect, {
		transform: booleanAttribute,
	});

	/** The selected values of the combobox. */
	public readonly value = model<T[] | undefined | null>(null);

	/**
	 * Determines whether a value is present (i.e., has been selected).
	 * Considers `undefined`, `null`, and empty string as "not present".
	 * Used to validate an item value on Enter key selection.
	 */
	public readonly isValuePresent = input<(value: T | undefined | null) => boolean>(this._config.isSingleValuePresent);

	public readonly hasValue = computed(() => {
		const value = this.value();
		if (value === null || value === undefined) return false;
		return value.length > 0;
	});

	/** The current search query. */
	public readonly search = model<string>('');

	private readonly _inputWidth = signal<number | null>(null);

	/** @internal The width of the search input wrapper */
	public readonly searchInputWrapperWidth = this._inputWidth.asReadonly();

	/** @internal Access all the items within the combobox */
	public readonly items = contentChildren<BrnComboboxItem<T>>(BrnComboboxItemToken, {
		descendants: true,
	});

	private readonly _content = contentChild<BrnComboboxContent>(BrnComboboxContent, {
		descendants: true,
	});

	/** Determine if the combobox has any visible items */
	public readonly visibleItems = computed(() => this.items().some((item) => item.visible()));

	/** @internal The key manager for managing active descendant */
	public readonly keyManager = new ActiveDescendantKeyManager(this.items, this._injector);

	/** @internal Whether the autocomplete is expanded */
	public readonly isExpanded = computed(() => this._brnPopover?.stateComputed() === 'open');

	private readonly _comboboxChipInput = signal<BrnComboboxChipInput<T> | undefined>(undefined);

	private readonly _comboboxList = signal<BrnComboboxList | undefined>(undefined);

	/** @internal The id of the combobox list, registered by BrnComboboxList. Used by the input for aria-controls. */
	public readonly listId = computed(() => this._comboboxList()?.id());

	/** @internal The id of the combobox content. Used by the trigger for aria-controls. */
	public readonly contentId = computed(() => this._content()?.id());

	public readonly mode = signal<ComboboxInputMode>('combobox').asReadonly();

	public readonly labelableId = computed(() => this._comboboxChipInput()?.id());

	protected _onChange?: ChangeFn<T[] | undefined | null>;
	protected _onTouched?: TouchFn;

	constructor() {
		this.keyManager
			.withVerticalOrientation()
			.withHomeAndEnd()
			.withPageUpDown()
			.withWrap()
			.skipPredicate((item) => item.disabled || !item.visible());

		this._brnPopover?.closed.subscribe(() => {
			this.search.set('');
			this.keyManager.setActiveItem(-1);
		});

		afterNextRender(() => {
			effect(
				() => {
					if (!this.autoHighlight() || !this.isExpanded() || !this.search()) return;

					const hasVisibleItems = this.visibleItems();

					untracked(() => {
						if (hasVisibleItems) {
							this.keyManager.setFirstItemActive();
						} else {
							this.keyManager.setActiveItem(-1);
						}
					});
				},
				{ injector: this._injector },
			);
		});
	}

	public registerComboboxChipInput(input: BrnComboboxChipInput<T>): void {
		this._comboboxChipInput.set(input);
	}

	/** @internal Register the combobox list. Called by BrnComboboxList in its constructor. */
	public registerComboboxList(list: BrnComboboxList): void {
		this._comboboxList.set(list);
	}

	public updateInputWidth(width: number | null): void {
		this._inputWidth.set(width);
	}

	public isSelected(itemValue: T): boolean {
		return this.value()?.some((v) => this.isItemEqualToValue()(itemValue, v)) ?? false;
	}

	public select(itemValue: T): void {
		const selected = this.value() ?? [];
		if (this.isSelected(itemValue)) {
			this.value.set(selected.filter((d) => !this.isItemEqualToValue()(d, itemValue)) ?? []);
		} else {
			this.value.set([...selected, itemValue]);
		}

		if (this.closeOnSelect() && this.search() !== '') {
			// reset search after selection and close popover
			this.search.set('');
			this.close();
		}

		this._onChange?.(this.value() ?? []);
	}

	/** Select the active item with Enter key. */
	public selectActiveItem(): void {
		if (!this.isExpanded()) return;

		const activeItem = this.keyManager.activeItem;

		// setActiveItem() bypasses skipPredicate, so the active item may be disabled.
		if (activeItem?.disabled) return;

		const value = activeItem?.value();

		if (this.isValuePresent()(value)) {
			this.select(value as T);
		} else {
			this.close();
		}
	}

	public resetValue(): void {
		this.value.set(null);
		this._onChange?.(null);
	}

	public resetSearch(): void {
		this.search.set('');
	}

	public removeValue(itemValue: T): void {
		const selected = this.value() ?? [];

		this.value.set(selected.filter((value) => !this.isItemEqualToValue()(itemValue, value)) ?? []);
		this._onChange?.(this.value());
	}

	public removeLastSelectedItem(): void {
		const selected = this.value() ?? [];
		if (selected.length === 0) return;

		this.value.set(selected.slice(0, -1));
		this._onChange?.(this.value());
	}

	public open(): void {
		if (this._disabled() || this.isExpanded()) return;

		this._brnPopover?.open();
	}

	protected _onFocusOut(event: FocusEvent): void {
		const currentTarget = event.currentTarget as HTMLElement;
		const focusedEl = event.relatedTarget as HTMLElement | null;
		const contentEl = this._content()?.el.nativeElement;

		if (!currentTarget.contains(focusedEl) && !contentEl?.contains(focusedEl)) {
			if (this.isExpanded()) {
				this._brnPopover?.close();
			}
			this._onTouched?.();
		}
	}

	public close(): void {
		if (this._disabled() || !this.isExpanded()) return;

		this._brnPopover?.close();
	}

	/** CONTROL VALUE ACCESSOR */
	public writeValue(value: T[] | undefined | null): void {
		this.value.set(value);
	}

	public registerOnChange(fn: ChangeFn<T[] | undefined | null>): void {
		this._onChange = fn;
	}

	public registerOnTouched(fn: TouchFn): void {
		this._onTouched = fn;
	}

	public setDisabledState(isDisabled: boolean) {
		this._disabled.set(isDisabled);
	}
}
